import { useMemo } from "react";
import classNames from "classnames";
import Icon from "@/components/Icon";
import Tag from "@/components/Tag";
import { useIsDashboardStackedTableRows } from "../_hooks/useIsDashboardSingleColumn";
import type { Path } from "@/router";
import ClickableEventRow, { ROW_BORDER } from "./ClickableEventRow";
import DashboardEventDate from "./DashboardEventDate";
import DashboardTableSkeleton from "./DashboardTableSkeleton";
import DashboardEventPanelHeader from "./DashboardEventPanelHeader";
import DashboardTextLink from "./DashboardTextLink";
import {
  buildScheduleTableRows,
  isScheduleEventRow,
  type ScheduleEventRow,
  type ScheduleTableRow
} from "../_utils/scheduleTableData";
import type { DashboardScheduleEvent } from "../_utils/dashboardEventDisplay";
import { getScheduleScopeDescription } from "../_utils/dashboardEventScopeLabels";
import { DASHBOARD_PANEL_BODY_CLASS, DASHBOARD_PANEL_SHELL_CLASS } from "../_utils/dashboardPanelStyles";

const EVENT_COLUMN_COUNT = 3;
const BODY_CELL = "px-4 py-2 h-12 align-middle text-left text-body-medium text-sys-brand-on-surface";
const DATE_CELL = "px-4 py-2 h-12 align-middle text-left whitespace-nowrap";

function ScheduleEventTitle({ title, draft }: { title: string; draft: boolean }) {
  if (!draft) {
    return <span>{title}</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span>{title}</span>
      <Tag tagType="surface" tagStyle="standard" size="small" testId="draft">
        Draft
      </Tag>
    </div>
  );
}

function ScheduleEventRegistration({ registration }: { registration?: string }) {
  if (!registration) {
    return null;
  }

  return (
    <span className="inline-flex items-center gap-1 pt-0.5 text-body-small text-sys-brand-on-surface-variant">
      <Icon name="group" size={16} />
      {registration}
    </span>
  );
}

function ScheduleEventRow({ event }: { event: ScheduleEventRow }) {
  const isStackedTableRows = useIsDashboardStackedTableRows();

  if (isStackedTableRows) {
    return (
      <ClickableEventRow slug={event.slug} title={event.title}>
        <td colSpan={EVENT_COLUMN_COUNT} className="px-4 py-3 align-top text-body-medium text-sys-brand-on-surface">
          <div className="flex flex-col gap-1">
            <DashboardEventDate date={event.date} />
            <div className="line-clamp-2">
              <ScheduleEventTitle title={event.title} draft={event.draft} />
            </div>
            <ScheduleEventRegistration registration={event.registration} />
          </div>
        </td>
      </ClickableEventRow>
    );
  }

  return (
    <ClickableEventRow slug={event.slug} title={event.title}>
      <td className={DATE_CELL}>
        <DashboardEventDate date={event.date} />
      </td>
      <td className={classNames(BODY_CELL)}>
        <ScheduleEventTitle title={event.title} draft={event.draft} />
      </td>
      <td className={classNames(BODY_CELL)}>
        {event.registration ? (
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <Icon name="group" size={16} />
            {event.registration}
          </span>
        ) : null}
      </td>
    </ClickableEventRow>
  );
}

function ScheduleTableRowItem({ row }: { row: ScheduleTableRow }) {
  if (!isScheduleEventRow(row)) {
    return (
      <tr className={classNames(ROW_BORDER, "bg-sys-brand-surface-container-high")} data-testid="schedule-month-row">
        <td
          colSpan={EVENT_COLUMN_COUNT}
          className="px-4 h-12 align-middle text-label-medium tracking-widest text-sys-brand-on-surface-variant"
        >
          {row.month}
        </td>
      </tr>
    );
  }

  return <ScheduleEventRow event={row} />;
}

type ScheduleTableProps = {
  eventsLink: Path;
  scheduleByMonth: { month: string; events: DashboardScheduleEvent[] }[];
  scopedSectionNames?: string[];
  loading?: boolean;
  error?: Error;
  missingSectionScope?: boolean;
};

export default function ScheduleTable({
  eventsLink,
  scheduleByMonth,
  scopedSectionNames = [],
  loading = false,
  error,
  missingSectionScope = false
}: ScheduleTableProps) {
  const rows = useMemo(() => buildScheduleTableRows(scheduleByMonth), [scheduleByMonth]);
  const scopeDescription = missingSectionScope ? null : getScheduleScopeDescription(scopedSectionNames);

  return (
    <section data-testid="dashboard-schedule-panel" className={DASHBOARD_PANEL_SHELL_CLASS}>
      <DashboardEventPanelHeader
        title="Schedule"
        scopeDescription={scopeDescription}
        testId="schedule-scope-description"
      />
      <div className={DASHBOARD_PANEL_BODY_CLASS}>
        <table data-testid="dashboard-schedule-table" className="w-full">
          <tbody>
            {loading ? (
              <DashboardTableSkeleton columnCount={EVENT_COLUMN_COUNT} />
            ) : missingSectionScope ? (
              <tr className={ROW_BORDER} data-testid="schedule-empty-scope-row">
                <td
                  colSpan={EVENT_COLUMN_COUNT}
                  className="px-4 py-6 text-center text-body-medium text-sys-brand-error"
                >
                  Unable to determine which sections to show events for.
                </td>
              </tr>
            ) : error ? (
              <tr className={ROW_BORDER} data-testid="schedule-error-row">
                <td
                  colSpan={EVENT_COLUMN_COUNT}
                  className="px-4 py-6 text-center text-body-medium text-sys-brand-error"
                >
                  Unable to load schedule. Please try again later.
                </td>
              </tr>
            ) : rows.length > 0 ? (
              rows.map((row) => <ScheduleTableRowItem key={row.id} row={row} />)
            ) : (
              <tr className={ROW_BORDER} data-testid="schedule-empty-row">
                <td
                  colSpan={EVENT_COLUMN_COUNT}
                  className="px-4 py-6 text-center text-body-medium text-sys-brand-on-surface-variant"
                >
                  No upcoming events
                </td>
              </tr>
            )}
            <tr className={ROW_BORDER} data-testid="schedule-view-more-row">
              <td colSpan={EVENT_COLUMN_COUNT} className="px-4 py-2 text-center">
                <DashboardTextLink to={eventsLink} testId="schedule-view-more">
                  View All
                </DashboardTextLink>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
